import type {NavList} from '@superweb/ui'
import type {TariffsZone} from '@yandex-delivery/api/model/tariffs'
import type {ComponentProps} from 'react'
import {useCallback, useMemo} from 'react'

import {type ZonesInfo} from '../../../../../../api'
import {useConfig} from '../../../../../../config'
import {useLinkProps} from '../../../../../../utils/use-link-props'

type NavListGroup = NonNullable<ComponentProps<typeof NavList>['groups']>

type UsePreparedCitiesParams = {
  zones: ZonesInfo
  country: string
}

/**
 * Хук для маппинга городов в формат для компонента NavList.
 */
export function usePreparedCities({zones, country}: UsePreparedCitiesParams) {
  const {getPath} = useConfig()
  const {getLinkProps} = useLinkProps()

  // Функция для преобразования города в NavList item
  const cityToNavListItem = useCallback(
    (city: TariffsZone) => {
      return {
        label: city.title,
        link: getLinkProps({
          href: getPath({country, city: city.id}),
          isCurrent: () => false,
        }),
      }
    },
    [country, getPath, getLinkProps],
  )

  // Подготавливаем списки городов для NavList
  const allCities = useMemo(() => {
    // Популярные города, которые будут отображаться сверху без заголовка группы
    const frequentItems = (zones.mainZones || []).map(cityToNavListItem)

    // Группируем остальные города по первой букве
    const otherGroups = zones.allZones.reduce<NavListGroup>((groups, city) => {
      const char = city.title.charAt(0).toUpperCase()
      const lastGroup = groups.at(-1)

      if (lastGroup && lastGroup.title === char) {
        lastGroup.items.push(cityToNavListItem(city))
      } else {
        groups.push({
          title: char,
          items: [cityToNavListItem(city)],
        })
      }

      return groups
    }, [])

    return {frequent: frequentItems, others: otherGroups}
  }, [zones, cityToNavListItem])

  return {allCities, cityToNavListItem}
}
